import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getHomepageSectionML } from '../../services/api';
import logo from '../../assets/bmp-logo-final-v2.png';

const NewFooter = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFooterContent();
  }, [language]);

  const loadFooterContent = async () => {
    setLoading(true);
    const SUPPORTED = ['pt', 'en', 'fr', 'ar', 'zh'];
    const base = (language || 'en').slice(0, 2).toLowerCase();
    const normalizedLang = SUPPORTED.includes(base) ? base : 'en';
    const result = await getHomepageSectionML('contact', normalizedLang);
    if (result.success) {
      setContent(result.data);
    }
    setLoading(false);
  };

  if (loading || !content) {
    return (
      <footer className="bg-[#2B2D42] text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400">Loading...</p>
        </div>
      </footer>
    );
  }

  const SUPPORTED = ['pt', 'en', 'fr', 'ar', 'zh'];
  const base = (language || 'en').slice(0, 2).toLowerCase();
  const code = SUPPORTED.includes(base) ? base : 'en';
  const pick = (link) => link[`name_${code}`] || link.name;

  const companyLinks = [
    { name: 'About', name_pt: 'Sobre Nós', name_fr: 'À propos', name_ar: 'من نحن', name_zh: '关于我们', href: '/about' },
    { name: 'Team', name_pt: 'Equipa', name_fr: 'Équipe', name_ar: 'الفريق', name_zh: '团队', href: '/team' },
    { name: 'Careers', name_pt: 'Carreiras', name_fr: 'Carrières', name_ar: 'الوظائف', name_zh: '招聘', href: '/careers' },
    { name: 'Contact', name_pt: 'Contacto', name_fr: 'Contact', name_ar: 'اتصل بنا', name_zh: '联系', href: '/contact' }
  ];

  const serviceLinks = [
    { name: 'Business Architecture', name_pt: 'Arquitetura Empresarial', name_fr: "Architecture d'entreprise", name_ar: 'الهندسة المؤسسية', name_zh: '企业架构', href: '/services/architecture' },
    { name: 'International Management', name_pt: 'Gestão Internacional', name_fr: 'Gestion internationale', name_ar: 'الإدارة الدولية', name_zh: '国际管理', href: '/services/management' },
    { name: 'Trading Solutions', name_pt: 'Soluções de Trading', name_fr: 'Solutions de trading', name_ar: 'حلول التداول', name_zh: '贸易解决方案', href: '/services/trading' },
    { name: 'Consulting', name_pt: 'Consultoria', name_fr: 'Conseil', name_ar: 'الاستشارات', name_zh: '咨询', href: '/services/consulting' }
  ];

  const locationsLabel = { pt: 'Localizações', en: 'Locations', fr: 'Emplacements', ar: 'المواقع', zh: '地点' }[code];
  const isPortuguese = language.startsWith('pt');

  return (
    <footer className="bg-[#2B2D42] text-white">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <img src={logo} alt="Grupo BMP" className="h-12 mb-6" />
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {content.tagline || (isPortuguese
                ? 'Excelência em arquitectura empresarial e gestão internacional desde 2008.'
                : 'Excellence in business architecture and international management since 2008.')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              {content.email && (
                <a href={`mailto:${content.email}`} className="flex items-center text-sm text-gray-400 hover:text-white transition-colors">
                  <Mail className="h-4 w-4 mr-2" />
                  {content.email}
                </a>
              )}
              {content.phone && (
                <a href={`tel:${content.phone}`} className="flex items-center text-sm text-gray-400 hover:text-white transition-colors">
                  <Phone className="h-4 w-4 mr-2" />
                  {content.phone}
                </a>
              )}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{content.company_links_label}</h3>
            <ul className="space-y-3">
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {pick(link)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">{content.services_links_label}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {pick(link)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="text-lg font-bold mb-6">
              {locationsLabel}
            </h3>
            <ul className="space-y-4">
              {content.locations?.map((location, index) => (
                <li key={index} className="flex items-start">
                  <MapPin className="h-4 w-4 mr-2 mt-1 text-[#00B4D8] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">{location.name}</p>
                    {location.role && <p className="text-xs text-[#48CAE4] mb-1">{location.role}</p>}
                    <p className="text-xs text-gray-400">{location.address}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Social Links */}
            <div className="flex gap-4">
              {content.social_linkedin && (
                <a href={content.social_linkedin} target="_blank" rel="noopener noreferrer" className="bg-gray-700 hover:bg-[#00B4D8] p-2 rounded-full transition-colors" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {content.social_instagram && (
                <a href={content.social_instagram} target="_blank" rel="noopener noreferrer" className="bg-gray-700 hover:bg-[#E4405F] p-2 rounded-full transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {content.social_facebook && (
                <a href={content.social_facebook} target="_blank" rel="noopener noreferrer" className="bg-gray-700 hover:bg-[#1877F2] p-2 rounded-full transition-colors" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {content.social_twitter && (
                <a href={content.social_twitter} target="_blank" rel="noopener noreferrer" className="bg-gray-700 hover:bg-[#00B4D8] p-2 rounded-full transition-colors" aria-label="Twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
            </div>

            {/* Copyright */}
            <p className="text-sm text-gray-400">
              {content.copyright}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewFooter;
